/**
 * Keeps Ads Manager status and timestamp presentation consistent across its
 * campaign, ad-group, and ad views.
 */
import React from 'react';
import Tag from '@amzn/meridian/tag';
import type { TagType } from '@amzn/meridian/tag/tag';
import { DateTime } from 'luxon';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import {
  BADGE_LABELS,
  PublisherEntityBadge,
  derivePublisherEntityStatus,
} from 'src/utils/publisherEntityStatus';

export const EMPTY_VALUE = '—';

export const STATUS_TAG_TYPES: Record<PublisherEntityBadge, TagType> = {
  [PublisherEntityBadge.FAILED]: 'error',
  [PublisherEntityBadge.LIVE]: 'success',
  [PublisherEntityBadge.PROCESSING]: 'theme',
  [PublisherEntityBadge.PENDING]: 'warning',
  [PublisherEntityBadge.SKIPPED]: 'neutral',
  [PublisherEntityBadge.REMOVED]: 'neutral',
};

export function formatUpdatedAt (updatedAt: string): string {
  const dateTime = DateTime.fromISO(updatedAt).toUTC();
  return dateTime.isValid
    ? dateTime.toLocaleString(DateTime.DATETIME_FULL)
    : EMPTY_VALUE;
}

interface StatusBadgeProps {
  entity: PublisherEntityDto;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ entity }) => {
  const { t } = useTranslation();
  const status = derivePublisherEntityStatus(entity);

  return <Tag type={STATUS_TAG_TYPES[status]}>{t(BADGE_LABELS[status])}</Tag>;
};
